import React, {useState, useEffect} from 'react';
import {
  Container,
  Row,
  Col,
  FormGroup,
  Input,
  Card,
  CardBody,
  CardTitle,
  CardSubtitle,
  Button,
  Label, Alert,
} from 'reactstrap';
import Dropzone from 'react-dropzone';
import {connect} from 'react-redux';
import {createPrinter} from '../../store/printer/actions';
import {Link, useNavigate} from 'react-router-dom';
import Gtx from './gtx';
import GtxPro from './gtxpro';
import GtxProBulk from './gtxprobulk';
import Gt341 from './gt341';
import Gt361 from './gt361';
import Gt381 from './gt381';
import Gtx600 from './gtx600';
import Gtx600sb from './gtx600sb';
import Gtx600nba from './gtx600nba';
import Wf1l640 from './wf1l640';
import Dtrx100 from './dtrx100';
import ListCustomers from '../../components/Lists/ListCustomers';
import {SweetAlert} from '../../components/Common/SweetAlert';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import axios from 'axios';
import authHeader from '../../helpers/jwt-token-access/auth-token-header';
import moment from 'moment';
import imageLogfile from '../../assets/images/icon-logfile.svg';
import FormField from '../../components/Common/FormField';
import {FormProvider, useForm} from 'react-hook-form';
import ListPrinterTypes from '../../components/Lists/ListPrinterTypes';
import Gtx300nc from './gtx300nc';

const PrinterAdd = (props) => {
  const SERIAL_PATTERNS: Record<string, RegExp> = {
    wf1l640: /^[A-Z][0-9][A-Z][0-9]+$/,
    dtrx100: /^[A-Z][0-9][9][0-9]+$/,
  };
  const DEFAULT_PATTERN = /^[A-Z][0-9]+$/;

  const SERIAL_HELP: Record<string, string> = {
    wf1l640: 'Example: H3L0043.',
    dtrx100: 'Example: A5900001.',
  };
  const DEFAULT_HELP = 'Example: A1234567.';

  const [selectedPrinterType, setSelectedPrinterType] = useState({});
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [errorMsg, setErrorMsg] = useState(false);
  const [successMsg, setSuccessMsg] = useState(false);
  const [errorFileMsg, setErrorFileMsg] = useState(false);
  const [serialStatus, setSerialStatus] = useState(true);
  const [existingPrinter, setExistingPrinter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();

  const form = useForm({
    mode: 'onChange',
  });
  const {
    handleSubmit, watch, register, formState: {errors},
  } = form;

  const printerSerialNo = watch('printer_serial_no');

  useEffect(() => {
    handleChangePrinterSerial();
  }, [printerSerialNo, selectedPrinterType]);

  useEffect(() => {
    setIsLoading(false);
    if (props.data && props.data.success) {
      setSuccessMsg(true);
    } else if (props.data && props.data.error) {
      setErrorMsg(props.data.error);
    }
  }, [props.data]);

  function handleSelectPrinterType(printerType) {
    setSelectedPrinterType(printerType);
  }

  function handleSelectCustomer(customer) {
    setSelectedCustomer(customer);
  }

  function handleChangePrinterSerial() {
    if (!printerSerialNo) return;

    const requiredLength = selectedPrinterType?.value === 'wf1l640' ? 7 : 8;

    if (printerSerialNo.length === requiredLength) {
      const url = process.env.REACT_APP_APIURL + 'printer/checkduplicate';
      const authToken = authHeader();

      const formData = new FormData();
      formData.append('serial', printerSerialNo);

      axios.post(url, formData, {headers: authToken})
          .then((response) => {
            setSerialStatus(response.status === 201);
            setExistingPrinter(null);
          })
          .catch((error) => {
            setSerialStatus(false);
            setExistingPrinter(error.response.data.printer.id);
          });
    }
  }

  function validateFiles(printerType, selectedFiles) {
    const hasTarget = (target) => selectedFiles.some((f) => f.target === target);

    if (['gtx600', 'gtx600sb', 'gtx600nba'].includes(printerType)) {
      if (!hasTarget('water')) return 'Please upload a water file';
    } else if (['wf1l640'].includes(printerType)) {
      if (!hasTarget('log') || !hasTarget('configuration')) {
        return 'Please upload a log and configuration file';
      }
    } else if (['dtrx100'].includes(printerType)) {
      if (!hasTarget('log')) {
        return 'Please upload a log file';
      }
    }

    return null;
  }

  function handleValidSubmit(values) {
    const printerType = selectedPrinterType.value;
    const fileError = validateFiles(printerType, selectedFiles);

    if (fileError) {
      setErrorFileMsg(fileError);
      return;
    }

    setIsLoading(true);
    const printer = {
      printer_serial_no: values.printer_serial_no,
      printer_type: selectedPrinterType.value,
      installation_date: values.installation_date,
      print_heads: values.print_heads[selectedPrinterType.value],
      customer_id: selectedCustomer.id,
      files: selectedFiles,
      ink_supply_serial:
        values.ink_supply_serial ? values.ink_supply_serial : null,
      r2r_unit_serial:
        values.r2r_unit_serial ? values.r2r_unit_serial : null,
      is_undamaged: values.is_undamaged ? values.is_undamaged : null,
      dryer_type: values.dryer_type ? values.dryer_type : null,
      dryer_brand: values.dryer_brand ? values.dryer_brand : null,
      dryer_model: values.dryer_model ? values.dryer_model : null,
      dryer_width: values.dryer_width ? values.dryer_width : null,
      dryer_serial: values.dryer_serial ? values.dryer_serial : null,
      rip_software_name: values.rip_software_name ? values.rip_software_name : null,
      rip_software_licence_no: values.rip_software_licence_no ? values.rip_software_licence_no : null,
      cutting_system_brand: values.cutting_system_brand ? values.cutting_system_brand : null,
      cutting_system_model: values.cutting_system_model ? values.cutting_system_model : null,
    };
    props.createPrinter(printer);
  }

  function handleAcceptedFiles(files, target) {
    files.map((file) => Object.assign(file, {
      preview: URL.createObjectURL(file),
      formattedSize: formatBytes(file.size),
      target: target,
      ext: file.name.split('.').reverse()[0],
    }));

    const tempFiles = [...selectedFiles];

    files.map((file) => tempFiles.push(file));

    setSelectedFiles(tempFiles);
  }

  function handleRemoveFile(index) {
    const tempFiles = [...selectedFiles];
    URL.revokeObjectURL(tempFiles[index].preview);
    tempFiles.splice(index, 1);
    setSelectedFiles(tempFiles);
  }

  function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>

          <Breadcrumbs parent="/printers" title="Printer"
            breadcrumbItem="Add Printer"/>

          <Row>
            <Col xs="12">
              <FormProvider {...form}>
                <form
                  className="form-horizontal"
                  onSubmit={handleSubmit(handleValidSubmit)}
                >
                  {errorMsg ?
                    <SweetAlert
                      title="Error!"
                      danger
                      showConfirm={true}
                      showCancel={false}
                      confirmBtnBsStyle="danger"
                      closeOnConfirm={true}
                      onConfirm={() => {
                        setErrorMsg(false);
                      }}
                    >
                      {errorMsg}
                    </SweetAlert> : ''
                  }
                  {successMsg ? <SweetAlert
                    title="Good job!"
                    success
                    showConfirm={true}
                    showCancel={false}
                    confirmBtnBsStyle="yellow"
                    onConfirm={() => {
                      setSuccessMsg(false);
                      navigate('/printers');
                    }}
                  >
                    The printer has been added!
                  </SweetAlert> : null}
                  {errorFileMsg ? <SweetAlert
                    title={errorFileMsg}
                    warning
                    showConfirm={true}
                    showCancel={false}
                    confirmBtnBsStyle="yellow"
                    onConfirm={() => {
                      setErrorFileMsg(false);
                    }}
                  >
                    Please upload required files.
                  </SweetAlert> : null}
                  <Card>
                    <CardBody>
                      <CardTitle>Printer</CardTitle>
                      <CardSubtitle className="mb-3">Select a Printer Type and type the Printer Serial
                        No.</CardSubtitle>
                      <FormGroup className="mb-4" row>
                        <Label md="2" className="col-form-label">Printer
                          Type *</Label>
                        <Col md="10">
                          <ListPrinterTypes onChange={(printerType) => {
                            handleSelectPrinterType(printerType);
                          }} required={true}/>
                        </Col>
                      </FormGroup>
                      <FormGroup className="mb-4" row>
                        <Label htmlFor="printer-serialno" md="2"
                          className="col-form-label">
                          Printer Serial No. *</Label>
                        <Col md="10">
                          <FormField
                            name="printer_serial_no"
                            className="form-control"
                            placeholder="Printer Serial No. *"
                            type="text"
                            validate={
                              {
                                required: {value: true},
                                pattern: {value: SERIAL_PATTERNS[selectedPrinterType?.value] ?? DEFAULT_PATTERN},
                                minLength: {value: selectedPrinterType?.value === 'wf1l640' ? 7 : 8},
                                maxLength: {value: selectedPrinterType?.value === 'wf1l640' ? 7 : 8},
                              }
                            }
                            onInput={
                              (e) => e.target.value = (
                                '' + e.target.value
                              ).toUpperCase()
                            }
                            maxLength={selectedPrinterType?.value === 'wf1l640' ? 7 : 8}
                            errorMessage="Please provide a valid serial number."
                            helpMessage={SERIAL_HELP[selectedPrinterType?.value] ?? DEFAULT_HELP}
                          />
                          {!serialStatus &&
                            <Alert color="danger">This serial number already exists! Do you want to <Link className="link-highlight" to={'/overtake-printer?id=' + existingPrinter}> overtake printer</Link>?</Alert>
                          }
                        </Col>
                      </FormGroup>
                      <FormGroup className="mb-4" row>
                        <Label htmlFor="is_undamaged" md="2">Printer is undamaged? *</Label>
                        <Col md="10">
                          <div className="form-group">
                            <select
                              className="form-control"
                              {...register('is_undamaged', {required: 'Please select an option'})}
                              defaultValue=""
                            >
                              <option value="">Select an option</option>
                              <option value="1">Yes</option>
                              <option value="0">No</option>
                            </select>
                            {errors.is_undamaged && (
                              <p className="text-danger"><small>{errors.is_undamaged.message}</small></p>
                            )}
                          </div>
                        </Col>
                      </FormGroup>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody>
                      <CardTitle>Customer</CardTitle>
                      <CardSubtitle className="mb-3">Choose a
                        customer</CardSubtitle>
                      <FormGroup className="mb-4" row>
                        <Label md="2"
                          className="col-form-label">Customer *</Label>
                        <Col md="10">
                          <ListCustomers required={true} onChange={(customer) => {
                            handleSelectCustomer(customer);
                          }}/>
                        </Col>
                      </FormGroup>
                      {selectedCustomer &&
                        <Row>
                          <Col sm="10">
                            <FormGroup>
                              <Label htmlFor="street">Street</Label>
                              <Input id="street" name="street" type="text"
                                value={selectedCustomer.street}
                                className="form-control"
                                disabled="disabled"/>
                            </FormGroup>
                          </Col>
                          <Col sm="2">
                            <FormGroup>
                              <Label htmlFor="street_no">No.</Label>
                              <Input id="street_no" name="street_no" type="text"
                                value={selectedCustomer.street_no}
                                className="form-control"
                                disabled="disabled"/>
                            </FormGroup>
                          </Col>
                          <Col sm="3">
                            <FormGroup>
                              <Label htmlFor="zip_code">Post code</Label>
                              <Input id="zip_code" name="zip_code"
                                type="text"
                                value={selectedCustomer.zip_code}
                                className="form-control"
                                disabled="disabled"/>
                            </FormGroup>
                          </Col>
                          <Col sm="6">
                            <FormGroup>
                              <Label htmlFor="city">City</Label>
                              <Input id="city" name="city" type="text"
                                value={selectedCustomer.city}
                                className="form-control"
                                disabled="disabled"/>
                            </FormGroup>
                          </Col>
                          <Col sm="3">
                            <FormGroup>
                              <Label htmlFor="country">Country</Label>
                              <Input id="country" name="country"
                                value={selectedCustomer.country}
                                type="text"
                                className="form-control"
                                disabled="disabled"/>
                            </FormGroup>
                          </Col>
                        </Row>
                      }
                      <div>
                        <p>Cant find your customer?</p>
                        <Link to="/add-customer"
                          className="btn btn-yellow">
                          <i className="bx bx-user-circle mr-1"></i> Add
                          new Customer </Link>
                      </div>
                    </CardBody>
                  </Card>
                  {selectedCustomer &&
                    <Card>
                      <CardBody>
                        <CardTitle>Printer Information</CardTitle>
                        <CardSubtitle className="mb-3">Fill all information
                          below</CardSubtitle>
                        <FormGroup className="mb-4" row>
                          <Label htmlFor="date-of-installation" md="2"
                            className="col-form-label">Installation Date of
                            Machine *</Label>
                          <Col md="10">
                            <FormField
                              name="installation_date"
                              className="form-control"
                              placeholder="Installation Date of Machine *"
                              type="date"
                              max={moment().format('YYYY-MM-DD')}
                              validate={{required: {value: true}}}
                              errorMessage="Provide a valid installation date."
                            />
                          </Col>
                        </FormGroup>
                        {selectedPrinterType.value === 'gtx' && (
                          <Gtx/>
                        )}
                        {selectedPrinterType.value === 'gtxpro' && (
                          <GtxPro/>
                        )}
                        {selectedPrinterType.value === 'gtxprobulk' && (
                          <GtxProBulk/>
                        )}
                        {selectedPrinterType.value === 'gtx300nc' && (
                          <Gtx300nc/>
                        )}
                        {selectedPrinterType.value === 'gtx600' && (
                          <Gtx600/>
                        )}
                        {selectedPrinterType.value === 'gtx600sb' && (
                          <Gtx600sb/>
                        )}
                        {selectedPrinterType.value === 'gtx600nba' && (
                          <Gtx600nba/>
                        )}
                        {['gtx600', 'gtx600sb', 'gtx600nba'].includes(selectedPrinterType.value) && (
                          <>
                            <div>
                              <CardTitle>Water quality check*</CardTitle>
                              <CardSubtitle className="mb-3">{'Please check' +
                                ' the water quality with the supplied' +
                                ' conductivity meter. Please upload a photo of ' +
                                'the result. Required conductivity of water is' +
                                ' < 10 μS/cm..'}</CardSubtitle>
                              <Dropzone
                                onDrop={(acceptedFiles) => {
                                  handleAcceptedFiles(acceptedFiles, 'water');
                                }}
                                accept=".pdf,.jpg,.jpeg,.png"
                                multiple={false}
                              >
                                {({getRootProps, getInputProps}) => (
                                  <div className="dropzone">
                                    <div
                                      className="dz-message needsclick"
                                      {...getRootProps()}
                                    >
                                      <input {...getInputProps()} />
                                      <div className="dz-message needsclick">
                                        <div className="mb-3">
                                          <i className="display-4 text-muted bx
                                        bxs-cloud-upload"/>
                                        </div>
                                        <h4>Drop file here or click to
                                          upload.</h4>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </Dropzone>
                              <div
                                className="dropzone-previews mt-3"
                                id="file-previews"
                              >
                                {selectedFiles.filter(
                                    (file) => file.target === 'water').map(
                                    (f, i) => {
                                      return (
                                        <Card
                                          className="mt-1 mb-0 shadow-none
                                      border dz-processing dz-image-preview
                                      dz-success dz-complete"
                                          key={i + '-file'}
                                        >
                                          <div className="p-2">
                                            <Row className="align-items-center">
                                              <Col className="col-auto">
                                                <img
                                                  data-dz-thumbnail=""
                                                  height="80"
                                                  className="avatar-sm rounded
                                                bg-light"
                                                  alt={f.name}
                                                  src={f.preview}
                                                />
                                              </Col>
                                              <Col>
                                                <Link
                                                  to="#"
                                                  className="text-muted
                                                font-weight-bold"
                                                >
                                                  {f.name}
                                                </Link>
                                                <p className="mb-0">
                                                  <strong>
                                                    {f.formattedSize}
                                                  </strong>
                                                </p>
                                              </Col>
                                            </Row>
                                          </div>
                                        </Card>
                                      );
                                    })}
                              </div>
                            </div>
                          </>
                        )}
                        {['wf1l640'].includes(selectedPrinterType.value) && (
                          <>
                            <Card className="shadow-none border mb-0">
                              <CardBody>
                                <CardTitle className="mb-3">Upload</CardTitle>
                                <CardSubtitle
                                  className="mb-3"
                                >
                                  In order to be able to add the printer, we need the log
                                  file and the configuration file.
                                </CardSubtitle>
                                <Row className="align-items-center">
                                  <Col>
                                    <div className="d-flex mb-4">
                                      <img alt={'icon log file'} src={imageLogfile} className="mr-2"/>
                                      <div className="align-self-center">
                                        <strong>
                                          1. Log file *
                                        </strong>
                                        <p
                                          className="mb-0">.pec</p>
                                      </div>
                                    </div>
                                  </Col>
                                  {['wf1l640'].includes(selectedPrinterType.value) && (
                                    <Col>
                                      <div className="d-flex mb-4">
                                        <img alt={'icon photo'} src={imageLogfile} className="mr-2"/>
                                        <div className="align-self-center">
                                          <strong>2. Configuration file *</strong>
                                          <p className="mb-0">.para</p>
                                        </div>
                                      </div>
                                    </Col>
                                  )}
                                </Row>
                                <Row>
                                  <Col>
                                    <Dropzone
                                      onDrop={(acceptedFiles) => {
                                        handleAcceptedFiles(acceptedFiles, 'log');
                                      }}
                                      accept={'.pec'}
                                      multiple={false}
                                    >
                                      {({getRootProps, getInputProps}) => (
                                        <div className="dropzone">
                                          <div
                                            className="dz-message needsclick"
                                            {...getRootProps()}
                                          >
                                            <input {...getInputProps()} />
                                            <div className="dz-message needsclick">
                                              <div className="mb-3">
                                                <i
                                                  className="display-4 text-muted bx
                                        bxs-cloud-upload"/>
                                              </div>
                                              <h4>Drop files here or click to upload.</h4>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </Dropzone>
                                  </Col>
                                  {['wf1l640'].includes(selectedPrinterType.value) && (
                                    <Col>
                                      <Dropzone
                                        onDrop={(acceptedFiles) => {
                                          handleAcceptedFiles(acceptedFiles, 'configuration');
                                        }}
                                        accept=".para"
                                        multiple={true}
                                      >
                                        {({getRootProps, getInputProps}) => (
                                          <div className="dropzone">
                                            <div
                                              className="dz-message needsclick"
                                              {...getRootProps()}
                                            >
                                              <input {...getInputProps()} />
                                              <div className="dz-message needsclick">
                                                <div className="mb-3">
                                                  <i
                                                    className="display-4 text-muted bx
                                        bxs-cloud-upload"
                                                  />
                                                </div>
                                                <h4>Drop files here or click to upload.</h4>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </Dropzone>
                                    </Col> )}
                                </Row>
                                <div
                                  className="dropzone-previews mt-3"
                                  id="file-previews"
                                >{selectedFiles.filter(
                                      (file) => file.target !== 'install').map(
                                      (f, i) => {
                                        return (
                                          <Card
                                            className="mt-1 mb-0 shadow-none border
                              dz-processing dz-image-preview dz-success
                              dz-complete"
                                            key={i + '-file'}
                                          >
                                            <div className="p-2">
                                              <Row className="align-items-center">
                                                <Col className="col-auto">
                                                  <img
                                                    data-dz-thumbnail=""
                                                    height="80"
                                                    className="avatar-sm rounded bg-light"
                                                    alt={f.name}
                                                    src={f.preview}
                                                  />
                                                  <i
                                                    className="bx bxs-file-txt font-size-24"
                                                  />
                                                </Col>
                                                <Col>
                                                  <div className="font-weight-bold">
                                                    {f.name}
                                                  </div>
                                                  <p className="mb-0">
                                                    <strong>{f.formattedSize}</strong>
                                                  </p>
                                                </Col>
                                                <Col>
                                                  <div
                                                    className="text-danger font-weight-bold
                                      text-right cursor-pointer"
                                                    onClick={() => handleRemoveFile(i)}>
                                                    <i
                                                      className="bx bx-x-circle font-size-24"
                                                    />
                                                  </div>
                                                </Col>
                                              </Row>
                                            </div>
                                          </Card>
                                        );
                                      })}
                                </div>
                              </CardBody>
                            </Card>
                          </>
                        )}
                        {['dtrx100'].includes(selectedPrinterType.value) && (
                          <>
                            <Card className="shadow-none border mb-0">
                              <CardBody>
                                <CardTitle className="mb-3">Upload</CardTitle>
                                <CardSubtitle
                                  className="mb-3"
                                >
                                  In order to be able to add the printer, we need the log
                                  file.
                                </CardSubtitle>
                                <Row className="align-items-center">
                                  <Col>
                                    <div className="d-flex mb-4">
                                      <img alt={'icon log file'} src={imageLogfile} className="mr-2"/>
                                      <div className="align-self-center">
                                        <strong>
                                          1. Log file *
                                        </strong>
                                        <p
                                          className="mb-0">.log</p>
                                      </div>
                                    </div>
                                  </Col>
                                </Row>
                                <Row>
                                  <Col>
                                    <Dropzone
                                      onDrop={(acceptedFiles) => {
                                        handleAcceptedFiles(acceptedFiles, 'log');
                                      }}
                                      accept={'.log'}
                                      multiple={false}
                                    >
                                      {({getRootProps, getInputProps}) => (
                                        <div className="dropzone">
                                          <div
                                            className="dz-message needsclick"
                                            {...getRootProps()}
                                          >
                                            <input {...getInputProps()} />
                                            <div className="dz-message needsclick">
                                              <div className="mb-3">
                                                <i
                                                  className="display-4 text-muted bx
                                        bxs-cloud-upload"/>
                                              </div>
                                              <h4>Drop files here or click to upload.</h4>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                    </Dropzone>
                                  </Col>
                                </Row>
                                <div
                                  className="dropzone-previews mt-3"
                                  id="file-previews"
                                >{selectedFiles.filter(
                                      (file) => file.target !== 'install').map(
                                      (f, i) => {
                                        return (
                                          <Card
                                            className="mt-1 mb-0 shadow-none border
                              dz-processing dz-image-preview dz-success
                              dz-complete"
                                            key={i + '-file'}
                                          >
                                            <div className="p-2">
                                              <Row className="align-items-center">
                                                <Col className="col-auto">
                                                  <img
                                                    data-dz-thumbnail=""
                                                    height="80"
                                                    className="avatar-sm rounded bg-light"
                                                    alt={f.name}
                                                    src={f.preview}
                                                  />
                                                  <i
                                                    className="bx bxs-file-txt font-size-24"
                                                  />
                                                </Col>
                                                <Col>
                                                  <div className="font-weight-bold">
                                                    {f.name}
                                                  </div>
                                                  <p className="mb-0">
                                                    <strong>{f.formattedSize}</strong>
                                                  </p>
                                                </Col>
                                                <Col>
                                                  <div
                                                    className="text-danger font-weight-bold
                                      text-right cursor-pointer"
                                                    onClick={() => handleRemoveFile(i)}>
                                                    <i
                                                      className="bx bx-x-circle font-size-24"
                                                    />
                                                  </div>
                                                </Col>
                                              </Row>
                                            </div>
                                          </Card>
                                        );
                                      })}
                                </div>
                              </CardBody>
                            </Card>
                          </>
                        )}
                        {selectedPrinterType.value === 'gt341' && (
                          <Gt341/>
                        )}
                        {selectedPrinterType.value === 'gt361' && (
                          <Gt361/>
                        )}
                        {selectedPrinterType.value === 'gt381' && (
                          <Gt381/>
                        )}
                        {selectedPrinterType.value === 'wf1l640' && (
                          <Wf1l640/>
                        )}
                        {selectedPrinterType.value === 'dtrx100' && (
                          <Dtrx100/>
                        )}
                      </CardBody>
                    </Card>
                  }
                  {selectedCustomer &&
                    <Card>
                      <CardBody>
                        <CardTitle>Upload</CardTitle>
                        <CardSubtitle className="mb-3">We recommend uploading a
                          document in which the
                          customer confirms that they have received a complete
                          installation. This
                          upload field is optional. This document can be the
                          dealer&apos;s own
                          document.</CardSubtitle>
                        <Dropzone
                          onDrop={(acceptedFiles) => {
                            handleAcceptedFiles(acceptedFiles, 'install');
                          }}
                          accept=".pdf"
                          multiple={false}
                        >
                          {({getRootProps, getInputProps}) => (
                            <div className="dropzone">
                              <div
                                className="dz-message needsclick"
                                {...getRootProps()}
                              >
                                <input {...getInputProps()} />
                                <div className="dz-message needsclick">
                                  <div className="mb-3">
                                    <i
                                      className="display-4 text-muted bx
                                    bxs-cloud-upload"
                                    />
                                  </div>
                                  <h4>Drop files here or click to upload.</h4>
                                </div>
                              </div>
                            </div>
                          )}
                        </Dropzone>
                        <div
                          className="dropzone-previews mt-3"
                          id="file-previews"
                        >
                          {selectedFiles.filter(
                              (file) => file.target === 'install').map(
                              (f, i) => {
                                return (
                                  <Card
                                    className="mt-1 mb-0 shadow-none border
                              dz-processing dz-image-preview
                              dz-success dz-complete"
                                    key={i + '-file'}
                                  >
                                    <div className="p-2">
                                      <Row className="align-items-center">
                                        <Col className="col-auto">
                                          <img
                                            data-dz-thumbnail=""
                                            height="80"
                                            className="avatar-sm rounded bg-light"
                                            alt={f.name}
                                            src={f.preview}
                                          />
                                        </Col>
                                        <Col>
                                          <Link
                                            to="#"
                                            className="text-muted
                                          font-weight-bold"
                                          >
                                            {f.name}
                                          </Link>
                                          <p className="mb-0">
                                            <strong>{f.formattedSize}</strong>
                                          </p>
                                        </Col>
                                        <Col>
                                          <div
                                            className="text-danger font-weight-bold
                                      text-right cursor-pointer"
                                            onClick={() => handleRemoveFile(i)}>
                                            <i
                                              className="bx bx-x-circle font-size-24"
                                            />
                                          </div>
                                        </Col>
                                      </Row>
                                    </div>
                                  </Card>
                                );
                              })}
                        </div>
                      </CardBody>
                    </Card>
                  }
                  <Row className="my-4">
                    <Col sm="6">
                      <Link to="/printers"
                        className="btn d-none d-sm-inline-block btn-link">
                        <i className="mdi mdi-arrow-left mr-1"></i> Back to
                        Printers Overview
                      </Link>
                    </Col>
                    <Col sm="6">
                      <div className="text-sm-right">
                        {isLoading ?
                          <Button className="btn btn-yellow" type="submit"
                            disabled={true}>
                            <i
                              className="bx bx-loader bx-spin font-size-16
                            align-middle mr-2"/> Add
                            Printer
                          </Button> :
                          <Button className="btn btn-yellow" type="submit">
                            <i className="mdi mdi-plus-thick mr-1"></i> Add
                            Printer
                          </Button>
                        }
                      </div>
                    </Col>
                  </Row>
                </form>
              </FormProvider>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};


const mapStatetoProps = (state) => {
  const {data} = state.Printer;
  state.Printer = {};
  return {data};
};

export default connect(mapStatetoProps, {createPrinter})(PrinterAdd);
